"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

export default function Preloader({ onComplete }) {
  const [isVisible, setIsVisible] = useState(true);
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });

  const text = "Saiful Full-Stack Developer";
  const words = text.split(" ");

  // Set window size only on client
  useEffect(() => {
    setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    const handleResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);

    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 2000);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [onComplete]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.2 },
    },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.45, ease: "easeInOut" } },
  };

  const wordVariants = {
    hidden: { opacity: 0, y: 30, rotateX: -60 },
    visible: { opacity: 1, y: 0, rotateX: 0, transition: { duration: 0.6, ease: [0.2, 0.8, 0.2, 1] } },
  };

  const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-base-100 dark:bg-base-300 transition-colors duration-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(18)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 rounded-full"
                style={{ background: "var(--p)", opacity: 0.12 }}
                initial={{
                  left: Math.random() * windowSize.width,
                  top: Math.random() * windowSize.height,
                  scale: 0,
                }}
                animate={{
                  top: (Math.random() * -120) | 0,
                  scale: [0, 1, 0],
                  opacity: [0, 0.9, 0],
                }}
                transition={{
                  duration: Math.random() * 3 + 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                  ease: "easeOut",
                }}
              />
            ))}
          </div>

          {/* Main content */}
          <motion.div className="text-center px-6" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
            {/* Animated logo */}
            <motion.div className="mb-6" initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}>
              <div className="w-16 h-16 mx-auto rounded-lg flex items-center justify-center shadow-lg bg-rose-500 text-white">
                <motion.div className="text-2xl font-bold" animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                  S
                </motion.div>
              </div>
            </motion.div>

            {/* Animated text */}
            <div className="space-y-2">
              {words.map((word, wi) => (
                <motion.div key={wi} className="flex justify-center space-x-1 flex-wrap" variants={wordVariants}>
                  {word.split("").map((letter, li) => (
                    <motion.span
                      key={li}
                      className={`text-3xl md:text-5xl font-extrabold leading-tight ${wi === 0 || wi === words.length - 1 ? "text-rose-500" : "text-white"}`}
                      variants={letterVariants}
                      whileHover={{ scale: 1.06, transition: { duration: 0.15 } }}
                    >
                      {letter}
                    </motion.span>
                  ))}
                  {wi < words.length - 1 && <motion.span className="text-3xl md:text-5xl font-extrabold text-neutral" variants={letterVariants}>&nbsp;</motion.span>}
                </motion.div>
              ))}
            </div>

            {/* Loading indicator */}
            <motion.div className="mt-10 flex justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7 }}>
              <div className="flex space-x-3">
                {[...Array(3)].map((_, i) => (
                  <motion.div key={i} className="w-3 h-3 rounded-full bg-rose-500" animate={{ y: [0, -8, 0], opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.1, repeat: Infinity, delay: i * 0.15, ease: "easeInOut" }} />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
